"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, Save, AlertCircle } from "lucide-react"
import { useVoiceAgents, type VoiceAgent, type CreateVoiceAgentData, type UpdateVoiceAgentData } from "@/hooks/agents/voice"
import { useVoices } from "@/hooks/voice"
import { VoiceSelector } from "./voice-selector"
import { RoleMultiSelect } from "@/components/agents/shared/role-multi-select"
import { RoleChangeConfirmDialog } from "@/components/agents/shared/role-change-confirm-dialog"
import { ResetToRoleDefaultButton } from "@/components/agents/shared/reset-to-role-default-button"
import { PromptHistoryButton } from "@/components/agents/shared/prompt-history-button"
import {
  AGENT_PRIMARY_ROLE_USE_CASE,
  DEFAULT_ROLE_SLUG,
  ROLE_SYSTEM_PROMPT_MAX_LENGTH,
} from "@/lib/ai/roles/types"
import { useAgentRolesQuery } from "@/hooks/queries/ai-use-case-roles"
import { useComposeAgentRolesMutation } from "@/hooks/mutations/ai-use-case-roles"
import { AI_USE_CASES } from "@/lib/ai/langchain/types"
import type { AiUseCaseRoleView } from "@/server-actions/ai-use-case-roles"
import {
  buildVoiceLanguageOptions,
  getVoiceLanguages,
  UNKNOWN_LANGUAGE_VALUE,
} from "@/lib/voice/voice-language-options"
import { VoiceLanguageSelector } from "./voice-language-selector"

interface AgentFormProps {
  agent: VoiceAgent | null
  onSave: (agent: VoiceAgent) => void
  onCancel?: () => void
  formId?: string
  hideButtons?: boolean
  onSavingChange?: (isSaving: boolean) => void
  onStatusChange?: (agentId: string, status: "active" | "disabled") => Promise<void>
  isUpdatingStatus?: boolean
}

// Field length limits
const LIMITS = {
  name: { min: 2, max: 100 },
  // Same ceiling as the API and role storage — a composed prompt must never be
  // unsavable in the form that received it.
  systemPrompt: { min: 10, max: ROLE_SYSTEM_PROMPT_MAX_LENGTH },
  firstMessage: { min: 5, max: 2000 },
}

export function AgentForm({ agent, onSave, onCancel, formId, hideButtons = false, onSavingChange, onStatusChange, isUpdatingStatus = false }: AgentFormProps) {
  const { createAgent, updateAgent } = useVoiceAgents()
  const { voices, isLoading: isLoadingVoices, error: voicesError } = useVoices()

  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const errorRef = useRef<HTMLDivElement>(null)

  // Form state
  const [name, setName] = useState(agent?.name || "")
  const [systemPrompt, setSystemPrompt] = useState(agent?.system_prompt || "")
  const [firstMessage, setFirstMessage] = useState(agent?.first_message || "")
  const [voiceId, setVoiceId] = useState(agent?.voice_id || "")
  const [languages, setLanguages] = useState(agent?.languages ?? ["en"])
  const [roles, setRoles] = useState<string[]>(agent?.config?.roles ?? [DEFAULT_ROLE_SLUG])
  const [roleLabel, setRoleLabel] = useState<string>(agent?.config?.role_label ?? "")
  // Last preset text filled in — untouched text is replaced silently, edits need confirm.
  const [lastRolePrompt, setLastRolePrompt] = useState<string | null>(null)
  // Roles awaiting the overwrite confirmation (prompt was hand-edited).
  const [pendingRoles, setPendingRoles] = useState<{ slugs: string[]; names: string[] } | null>(null)

  const { data: voiceRoles = [] } = useAgentRolesQuery(AGENT_PRIMARY_ROLE_USE_CASE.voice_agent)
  const compose = useComposeAgentRolesMutation()

  const applyRolePrompt = (nextRoles: string[], prompt: string) => {
    setSystemPrompt(prompt)
    setLastRolePrompt(prompt)
    setRoles(nextRoles)
  }

  // Creation: prefill the empty prompt with the default preset once roles load.
  useEffect(() => {
    if (agent || systemPrompt.trim() || lastRolePrompt !== null || roles.length !== 1) return
    const preset = voiceRoles.find((r) => r.slug === roles[0])
    if (preset) {
      setSystemPrompt(preset.system_prompt)
      setLastRolePrompt(preset.system_prompt)
    }
  }, [agent, voiceRoles, roles, systemPrompt, lastRolePrompt])

  /**
   * Voice has one prompt source — this field. A single role fills it from the
   * preset (free, instant); several roles have to be merged by the model first.
   */
  const fillPromptFromRoles = async (nextRoles: string[]) => {
    if (nextRoles.length === 1) {
      const preset = voiceRoles.find((r) => r.slug === nextRoles[0])
      // No preset in the loaded list (deleted platform-side): keep the current text
      // rather than blanking the only prompt source this channel has.
      if (preset) applyRolePrompt(nextRoles, preset.system_prompt)
      else setRoles(nextRoles)
      return
    }
    try {
      const bundle = await compose.mutateAsync({ scope_type: "voice_agent", role_slugs: nextRoles })
      const prompt = bundle[AI_USE_CASES.VOICE_AGENT]?.system_prompt
      if (!prompt) throw new Error("The roles produced no prompt")
      applyRolePrompt(nextRoles, prompt)
    } catch (composeError) {
      // The role set is only recorded once its prompt exists — saving a new set with
      // the previous prompt is the one divergence the server does not backstop.
      setError(composeError instanceof Error ? composeError.message : "Failed to compose the prompt")
    }
  }

  const handleRolesChange = (nextRoles: string[], roleData: AiUseCaseRoleView[]) => {
    const untouched = !systemPrompt.trim() || systemPrompt === lastRolePrompt
    if (!untouched) {
      setPendingRoles({
        slugs: nextRoles,
        names: nextRoles.map((slug) => roleData.find((r) => r.slug === slug)?.name ?? slug),
      })
      return
    }
    void fillPromptFromRoles(nextRoles)
  }

  const languageOptions = useMemo(
    () =>
      buildVoiceLanguageOptions(voices).filter(
        (option) => option.value !== UNKNOWN_LANGUAGE_VALUE,
      ),
    [voices],
  )

  const filteredVoices = useMemo(() => {
    if (languages.length === 0) {
      return voices
    }

    return voices.filter((voice) => {
      const supportedLanguages = getVoiceLanguages(voice).map((code) =>
        code.toLowerCase(),
      )
      if (supportedLanguages.length === 0) return true
      return languages.every((language) =>
        supportedLanguages.includes(language),
      )
    })
  }, [voices, languages])

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [error])

  // A pending composition counts as busy: the form is about to replace the prompt
  // AND the role set, so saving mid-flight persists the pair the user is discarding.
  const isBusy = isSaving || compose.isPending

  useEffect(() => {
    onSavingChange?.(isBusy)
  }, [isBusy, onSavingChange])

  useEffect(() => {
    if (languageOptions.length === 0) {
      return
    }

    const available = new Set(languageOptions.map((option) => option.value))
    const validLanguages = languages.filter((language) =>
      available.has(language),
    )
    if (validLanguages.length === languages.length && languages.length > 0) {
      return
    }
    const fallback =
      languageOptions.find((option) => option.value === "en")?.value ||
      languageOptions[0].value
    setLanguages(validLanguages.length > 0 ? validLanguages : [fallback])
  }, [languageOptions, languages])

  useEffect(() => {
    if (languages.length === 0 || !voiceId) {
      return
    }

    // Clear a voice that doesn't match the selected language.
    const selectedVoice = voices.find((voice) => voice.id === voiceId)
    const selectedLanguages = selectedVoice
      ? getVoiceLanguages(selectedVoice).map((code) => code.toLowerCase())
      : []
    if (
      selectedLanguages.length > 0 &&
      !languages.every((language) => selectedLanguages.includes(language))
    ) {
      setVoiceId("")
    }
  }, [languages, voiceId, voices])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (compose.isPending) return
    setError(null)

    // Validation with length checks
    const trimmedName = name.trim()
    const trimmedSystemPrompt = systemPrompt.trim()
    const trimmedFirstMessage = firstMessage.trim()

    if (!trimmedName) {
      setError("Agent name is required")
      return
    }
    if (trimmedName.length < LIMITS.name.min || trimmedName.length > LIMITS.name.max) {
      setError(`Agent name must be between ${LIMITS.name.min} and ${LIMITS.name.max} characters`)
      return
    }
    if (!trimmedSystemPrompt) {
      setError("System prompt is required")
      return
    }
    if (trimmedSystemPrompt.length < LIMITS.systemPrompt.min) {
      setError(`System prompt must be at least ${LIMITS.systemPrompt.min} characters`)
      return
    }
    if (trimmedSystemPrompt.length > LIMITS.systemPrompt.max) {
      setError(`System prompt must not exceed ${LIMITS.systemPrompt.max} characters`)
      return
    }
    if (!trimmedFirstMessage) {
      setError("First message is required")
      return
    }
    if (trimmedFirstMessage.length < LIMITS.firstMessage.min) {
      setError(`First message must be at least ${LIMITS.firstMessage.min} characters`)
      return
    }
    if (trimmedFirstMessage.length > LIMITS.firstMessage.max) {
      setError(`First message must not exceed ${LIMITS.firstMessage.max} characters`)
      return
    }
    if (!voiceId) {
      setError("Please select a voice")
      return
    }
    if (languages.length === 0) {
      setError("Please select at least one language")
      return
    }

    setIsSaving(true)

    try {
      let savedAgent: VoiceAgent

      if (agent) {
        // Update existing agent
        const updates: UpdateVoiceAgentData = {
          name: name.trim(),
          system_prompt: systemPrompt.trim(),
          first_message: firstMessage.trim(),
          voice_id: voiceId,
          languages,
          roles,
          role_label: roleLabel.trim() || null,
        }
        savedAgent = await updateAgent(agent.id, updates)
      } else {
        // Create new agent
        const data: CreateVoiceAgentData = {
          name: name.trim(),
          system_prompt: systemPrompt.trim(),
          first_message: firstMessage.trim(),
          voice_id: voiceId,
          languages,
          roles,
          role_label: roleLabel.trim() || null,
        }
        savedAgent = await createAgent(data)
      }

      onSave(savedAgent)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save agent")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-6 overflow-hidden">
      {error && (
        <Alert ref={errorRef} className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {/* Name + Role */}
      <div className={`grid gap-x-6 gap-y-4 sm:grid-cols-[3fr_2fr] sm:items-start ${agent && agent.status !== "error" && onStatusChange ? "lg:grid-cols-[3fr_2fr_1.5fr]" : ""}`}>
        <div className="space-y-2">
          <Label htmlFor="name">Agent Name *</Label>
          <Input
            id="name"
            placeholder="e.g., Customer Support Agent"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={LIMITS.name.max}
          />
          <p className="text-xs text-gray-500 text-right">
            {name.length}<span>/</span>{LIMITS.name.max}
          </p>
        </div>

        {/* Roles — prefill the system prompt (merged by AI when there are several) */}
        <RoleMultiSelect
          useCase={AGENT_PRIMARY_ROLE_USE_CASE.voice_agent}
          value={roles}
          onChange={handleRolesChange}
          roleLabel={roleLabel}
          onRoleLabelChange={setRoleLabel}
          loading={compose.isPending}
        />
        {agent && agent.status !== "error" && onStatusChange && (
          <div className="space-y-2 sm:col-span-2 lg:col-span-1">
            <Label htmlFor="voice-agent-status">Status</Label>
            <Select
              value={agent.status === "disabled" ? "disabled" : "active"}
              onValueChange={(status) => void onStatusChange(agent.id, status as "active" | "disabled")}
              disabled={isUpdatingStatus}
            >
              <SelectTrigger
                id="voice-agent-status"
                className={agent.status === "disabled" ? "border-amber-300 bg-amber-50" : "border-green-300 bg-green-50"}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active"><span>Active</span></SelectItem>
                <SelectItem value="disabled"><span>Disabled</span></SelectItem>
              </SelectContent>
            </Select>
            {agent.status === "disabled" && <p className="text-xs text-muted-foreground">Requires voice access and an available agent slot.</p>}
          </div>
        )}
      </div>

      <VoiceLanguageSelector
        options={languageOptions}
        value={languages}
        onChange={setLanguages}
        disabled={isLoadingVoices || Boolean(voicesError)}
        loading={isLoadingVoices}
        error={voicesError}
      />

      {/* Voice */}
      <VoiceSelector
        voices={filteredVoices}
        selectedVoiceId={voiceId}
        onSelect={(selectedId) => {
          setVoiceId(selectedId)
          if (languages.length === 0) {
            const selectedVoice = voices.find((voice) => voice.id === selectedId)
            const selectedLanguages = selectedVoice ? getVoiceLanguages(selectedVoice) : []
            if (selectedLanguages.length > 0) {
              setLanguages([selectedLanguages[0].toLowerCase()])
            }
          }
        }}
        isLoading={isLoadingVoices}
        error={voicesError}
      />

      <RoleChangeConfirmDialog
        open={Boolean(pendingRoles)}
        onOpenChange={(open) => !open && setPendingRoles(null)}
        roleNames={pendingRoles?.names ?? []}
        overwrites={["system prompt"]}
        onConfirm={(applyPreset) => {
          const nextRoles = pendingRoles?.slugs ?? []
          setPendingRoles(null)
          if (!nextRoles.length) return
          // Declining the overwrite keeps the hand-edited prompt and only records the roles.
          if (applyPreset) void fillPromptFromRoles(nextRoles)
          else setRoles(nextRoles)
        }}
      />

      {/* System Prompt */}
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Label htmlFor="systemPrompt">System Prompt *</Label>
          <div className="flex flex-wrap items-center gap-2">
            {/* History exists only for a saved agent — a draft has no versions yet. */}
            {agent?.id && (
              <PromptHistoryButton
                scopeType="voice_agent"
                scopeId={agent.id}
                useCase={AGENT_PRIMARY_ROLE_USE_CASE.voice_agent}
                currentPrompt={systemPrompt}
                onRestore={setSystemPrompt}
                canRestore
              />
            )}
            <ResetToRoleDefaultButton
              scopeType="voice_agent"
              useCase={AGENT_PRIMARY_ROLE_USE_CASE.voice_agent}
              roleSlugs={roles}
              roleLabel={roleLabel}
              onReset={(composed) => {
                setSystemPrompt(composed.system_prompt)
                setLastRolePrompt(composed.system_prompt)
              }}
            />
          </div>
        </div>
        <Textarea
          id="systemPrompt"
          className="md:h-[236px]"
          placeholder="You are a helpful customer support agent..."
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          rows={5}
          maxLength={LIMITS.systemPrompt.max}
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>Instructions for how the agent should behave and respond.</span>
          <span className={systemPrompt.length > LIMITS.systemPrompt.max * 0.9 ? "text-yellow-600" : ""}>
            {systemPrompt.length}/{LIMITS.systemPrompt.max}
          </span>
        </div>
      </div>

      {/* First Message */}
      <div className="space-y-2">
        <Label htmlFor="firstMessage">First Message *</Label>
        <Textarea
          id="firstMessage"
          placeholder="Hello! Thank you for calling. How can I help you today?"
          value={firstMessage}
          onChange={(e) => setFirstMessage(e.target.value)}
          rows={3}
          maxLength={LIMITS.firstMessage.max}
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>The greeting message the agent will say when answering a call.</span>
          <span className={firstMessage.length > LIMITS.firstMessage.max * 0.9 ? "text-yellow-600" : ""}>
            {firstMessage.length}/{LIMITS.firstMessage.max}
          </span>
        </div>
      </div>

      {/* Actions */}
      {!hideButtons && (
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isBusy} className="bg-blue-600 hover:bg-blue-700">
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {agent ? "Update Agent" : "Create Agent"}
              </>
            )}
          </Button>
        </div>
      )}
    </form>
  )
}
